import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useBlocker } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { useMoveStorageNode, useRelabelGrid, useStorageConditions, useUpdateStorageNode } from '@/hooks/useStorage'
import { useProfiles } from '@/hooks/useInventoryMaster'
import { useStorageZones } from '@/hooks/usePickLists'
import { inheritedKeptAt, keptAtOptions, profileDisplayName } from '@/lib/keptAt'
import { DEFAULT_LABEL_PREFIX } from '@/lib/slotLabels'
import { storageTemperaturesHref } from '@/lib/storageRoutes'
import {
  applyEdits,
  changeCount,
  draftFromNode,
  dropSaved,
  inheritedZone,
  moveTargets,
  runSettingsOps,
  settingsOps,
  slotNamePreview,
  type SettingsEdits,
  type SettingsOp,
} from '@/lib/storageSettingsForm'
import { isOccupiable } from '@/lib/storageTree'
import { settingsTitle } from '@/lib/storageUnitView'
import { cn } from '@/lib/utils'
import { STORAGE_NODE_KIND_LABELS, type ApiStorageNode, type SlotLabelScheme } from '@/types/storage'
import { FAMILY_DOT, KeptAtBadge } from '../KeptAtBadge'
import { sanitizeCode } from '../LayoutControls'
import { useUndoToast } from '../UndoToast'
import { useLocationText } from '@/hooks/useStoragePlaces'

const SCHEMES: { value: SlotLabelScheme; label: string }[] = [
  { value: 'ROW_COLUMN', label: 'A1, A2… (rows & columns)' },
  { value: 'SEQUENTIAL', label: '1, 2, 3… (in order)' },
]

/** What each step of a save was doing, for the message when it is refused. */
const OP_WHAT: Record<SettingsOp['kind'], string> = {
  update: 'save the code, name, Kept at or zone',
  relabel: 'rename the slots',
  move: 'move it',
}

const changesLabel = (n: number) => (n === 1 ? '1 change not saved' : `${n} changes not saved`)

/**
 * A storage node's settings, as one modal with one Save.
 *
 * Every field edits a draft; nothing is sent until Save, which turns the draft
 * into the short list of requests it needs (`settingsOps`) and runs them in
 * order, stopping at the first refusal. Only the user's own edits are held
 * (`SettingsEdits`), laid over the node as it now is — so after a save that
 * half worked, the fields that went through read the refetched node and only
 * the rest stay dirty.
 *
 * Basics, Storage, Slots and Move need `inventory.configure`; Replace and
 * Delete are passed in only when the user may do them. Closing, or leaving the
 * page, with unsaved edits asks first — in the app, not `window.confirm`.
 */
export function NodeSettingsForm({
  node,
  chain,
  nodes,
  isUnit,
  canConfigure,
  onReplace,
  onDelete,
  onDone,
}: {
  node: ApiStorageNode
  /** Root→node, the node last. What it would inherit is read from this. */
  chain: ApiStorageNode[]
  /** The unit's containers, as loaded — where it may be moved to. */
  nodes: ApiStorageNode[]
  /** The unit itself — titled "Unit settings", with no path to show. */
  isUnit: boolean
  canConfigure: boolean
  /** Replace unit…, when the user may and this is a unit with equipment. */
  onReplace?: () => void
  /** Delete…, when the user may. */
  onDelete?: () => void
  /** Close the modal: after a full save, or once the user lets go of edits. */
  onDone: () => void
}) {
  const locate = useLocationText()
  const update = useUpdateStorageNode()
  const relabel = useRelabelGrid()
  const move = useMoveStorageNode()
  const showToast = useUndoToast()

  const [edits, setEdits] = useState<SettingsEdits>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Asked before closing with edits: what to do once the user lets them go.
  const [confirming, setConfirming] = useState<{ then?: () => void } | null>(null)
  // Set the moment the user lets go of their edits (or they are saved), so
  // the close that follows in the same tick is not itself blocked.
  const released = useRef(false)

  const draft = applyEdits(node, edits)
  const count = changeCount(node, draft)
  const dirty = count > 0
  const edit = (patch: SettingsEdits) => setEdits((prev) => ({ ...prev, ...patch }))

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !released.current &&
      dirty &&
      (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search),
  )
  useEffect(() => {
    if (!dirty) return
    // `returnValue` as well: older browsers only prompt when it is set.
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  /** Close, asking first when there is something to lose; then `then`. */
  const requestClose = (then?: () => void) => {
    if (saving) return
    if (dirty) return setConfirming({ then })
    released.current = true
    onDone()
    then?.()
  }

  const discard = () => {
    const then = confirming?.then
    setConfirming(null)
    released.current = true
    onDone()
    then?.()
  }

  const code = sanitizeCode(draft.code)
  const isSlot = isOccupiable(node.kind)
  const hasGrid = node.gridRows != null && node.gridColumns != null

  async function save() {
    setSaving(true)
    setError(null)
    const ops = settingsOps(node, { ...draft, code })
    const outcome = await runSettingsOps(ops, async (op) => {
      if (op.kind === 'update') await update.mutateAsync({ id: node.id, ...op.patch })
      else if (op.kind === 'relabel')
        await relabel.mutateAsync({ id: node.id, scheme: op.scheme, ...(op.prefix ? { prefix: op.prefix } : {}) })
      else await move.mutateAsync({ id: node.id, newParentId: op.newParentId })
    })
    setEdits((prev) => dropSaved(prev, outcome.done))
    setSaving(false)
    if (outcome.failed) {
      // Never "saved" on a partial result: say what stopped, and what did not.
      const lead = outcome.done.length > 0 ? 'Some changes were saved, but it could not' : 'Could not'
      setError(`${lead} ${OP_WHAT[outcome.failed.op.kind]}: ${outcome.failed.message}`)
      return
    }
    showToast('Settings saved')
    released.current = true
    onDone()
  }

  return (
    <>
      <Dialog open onOpenChange={(open) => !open && requestClose()}>
        <DialogContent className='flex max-h-[90vh] flex-col gap-0 p-0 sm:max-w-2xl'>
          <DialogHeader className='space-y-1 border-b px-6 py-4'>
            <DialogTitle>{settingsTitle(node, isUnit)}</DialogTitle>
            <DialogDescription>
              {isUnit ? (node.name ?? 'The whole unit and everything in it.') : locate(node.path)}
            </DialogDescription>
          </DialogHeader>

          <fieldset disabled={saving} className='min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-5'>
            {canConfigure && (
              <Section title='Basics'>
                <Field
                  label='Code and name'
                  htmlFor='settings-code'
                  help='Changing the code updates the address of everything inside.'
                >
                  <div className='flex gap-2'>
                    <Input
                      id='settings-code'
                      aria-label='Code'
                      className='w-28 shrink-0 font-medium'
                      value={draft.code}
                      onChange={(e) => edit({ code: e.target.value })}
                      onBlur={() => edit({ code })}
                    />
                    <Input
                      aria-label='Name'
                      className='min-w-0 flex-1'
                      value={draft.name}
                      placeholder='Descriptive name (optional)'
                      onChange={(e) => edit({ name: e.target.value })}
                    />
                  </div>
                  {!code && <p className='text-xs text-destructive'>A code is required.</p>}
                </Field>
              </Section>
            )}

            {/* A slot is never kept at a temperature of its own, nor on a walk
                of its own — the box or the unit is. */}
            {canConfigure && !isSlot && (
              <Section title='Storage'>
                <div className='grid gap-5 sm:grid-cols-2'>
                  <KeptAtField node={node} chain={chain} value={draft.keptAt} onChange={(keptAt) => edit({ keptAt })} />
                  <ZoneField chain={chain} value={draft.zoneId} onChange={(zoneId) => edit({ zoneId })} />
                </div>
              </Section>
            )}

            {canConfigure && hasGrid && (
              <Section title='Slots'>
                <SlotNamesField
                  node={node}
                  scheme={draft.scheme}
                  prefix={draft.prefix}
                  onChange={(next) => edit(next)}
                />
              </Section>
            )}

            <OtherActions
              node={node}
              nodes={canConfigure ? nodes : []}
              parentId={draft.parentId}
              onMove={(parentId) => edit({ parentId })}
              onReplace={onReplace && (() => requestClose(onReplace))}
              onDelete={onDelete && (() => requestClose(onDelete))}
            />
          </fieldset>

          {error && (
            <p role='alert' className='border-t bg-destructive/5 px-6 py-2.5 text-sm text-destructive'>
              {error}
            </p>
          )}

          <div className='flex flex-wrap items-center justify-between gap-3 border-t px-6 py-3'>
            <p className='text-sm text-muted-foreground'>{dirty ? changesLabel(count) : ''}</p>
            <div className='flex gap-2'>
              {canConfigure ? (
                <>
                  <Button variant='outline' disabled={saving} onClick={() => requestClose()}>
                    Cancel
                  </Button>
                  <Button disabled={!dirty || !code || saving} onClick={() => void save()}>
                    {saving ? 'Saving…' : 'Save changes'}
                  </Button>
                </>
              ) : (
                <Button variant='outline' onClick={() => requestClose()}>
                  Close
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* One prompt for closing the modal and for leaving the page. */}
      <Dialog
        open={confirming !== null || blocker.state === 'blocked'}
        onOpenChange={(open) => {
          if (open) return
          setConfirming(null)
          blocker.reset?.()
        }}
      >
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>Discard unsaved changes?</DialogTitle>
            <DialogDescription>
              {changesLabel(count)} on {node.code}.
            </DialogDescription>
          </DialogHeader>
          <div className='flex justify-end gap-2'>
            <Button
              variant='outline'
              onClick={() => {
                setConfirming(null)
                blocker.reset?.()
              }}
            >
              Keep editing
            </Button>
            <Button
              variant='destructive'
              onClick={() => {
                if (blocker.state === 'blocked') {
                  // The edits go; the guard stays, for whatever is edited next
                  // if this navigation leaves the modal open (another `?node=`).
                  setEdits({})
                  blocker.proceed?.()
                } else discard()
              }}
            >
              Discard changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className='space-y-3'>
      <h3 className='text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>{title}</h3>
      {children}
    </section>
  )
}

/** Label on top, the control full width below, one short help line under it. */
function Field({
  label,
  htmlFor,
  help,
  children,
}: {
  label: string
  htmlFor?: string
  help?: ReactNode
  children: ReactNode
}) {
  return (
    <div className='min-w-0 space-y-1.5'>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {help && <p className='text-xs text-muted-foreground'>{help}</p>}
    </div>
  )
}

/**
 * Kept at: on by default, the switch takes the temperature from above; off,
 * the node names its own profile. A unit has nothing above it, so it only has
 * the picker.
 *
 * A legacy value (a condition set directly, no profile) shows as it is, and
 * stays until the user picks a profile or switches inheriting on — the draft
 * never reads it as "inherit" on its own.
 */
function KeptAtField({
  node,
  chain,
  value,
  onChange,
}: {
  node: ApiStorageNode
  chain: ApiStorageNode[]
  value: string
  onChange: (value: string) => void
}) {
  const profiles = useProfiles('storage')
  const conditions = useStorageConditions()
  const options = useMemo(
    () => keptAtOptions(profiles.data ?? [], conditions.data?.conditions ?? []),
    [profiles.data, conditions.data],
  )
  const inherited = inheritedKeptAt(chain)
  // Switched off with no profile picked yet: the draft is still INHERIT, so
  // nothing counts as changed until a temperature is chosen.
  const [choosing, setChoosing] = useState(false)
  const inheriting = inherited != null && value === 'INHERIT' && !choosing
  const kind = STORAGE_NODE_KIND_LABELS[node.kind].toLowerCase()
  const opening = draftFromNode(node).keptAt

  return (
    <Field
      label='Kept at'
      htmlFor={inherited ? 'settings-kept-at-inherit' : undefined}
      help={inherited ? `Turn off to give this ${kind} its own temperature.` : undefined}
    >
      {inherited && (
        <div className='flex items-center gap-2.5 rounded-md border px-3 py-2'>
          <Switch
            id='settings-kept-at-inherit'
            checked={inheriting}
            onCheckedChange={(on) => {
              if (on) {
                setChoosing(false)
                onChange('INHERIT')
              } else if (opening !== 'INHERIT') {
                // Back to what it had — its own profile, or the legacy value.
                onChange(opening)
              } else {
                setChoosing(true)
              }
            }}
          />
          <span className='flex min-w-0 flex-wrap items-center gap-1.5 text-sm'>
            Same as {inherited.fromName}: <KeptAtBadge condition={inherited.condition} />
          </span>
        </div>
      )}
      {!inheriting && (
        <Select value={value === 'INHERIT' || value === 'LEGACY' ? '' : value} onValueChange={onChange}>
          <SelectTrigger aria-label='Kept at'>
            <SelectValue placeholder='Choose a temperature…' />
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o.profileId} value={o.profileId}>
                <span className='flex items-center gap-2'>
                  <span
                    className={cn('h-2 w-2 shrink-0 rounded-full', o.family ? FAMILY_DOT[o.family] : 'bg-muted-foreground')}
                  />
                  <span className='tabular-nums font-medium'>{o.temperature}</span>
                  <span className='text-muted-foreground'>{profileDisplayName(o.name)}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {value === 'LEGACY' && (
        <p className='flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground'>
          <KeptAtBadge condition={node.providesCondition} />
          Set directly — choosing one replaces it.
        </p>
      )}
      {!inheriting && (
        <Link to={storageTemperaturesHref('options')} className='inline-block text-xs font-medium text-primary hover:underline'>
          Manage temperatures →
        </Link>
      )}
    </Field>
  )
}

/** The picking zone: the one from above, or one of its own. */
function ZoneField({
  chain,
  value,
  onChange,
}: {
  chain: ApiStorageNode[]
  value: string
  onChange: (value: string) => void
}) {
  const zones = useStorageZones()
  // Active zones, plus the one it is on now even if since retired, so the
  // select never shows blank over a real value.
  const choices = (zones.data ?? []).filter((z) => z.active || z.id === value)
  // Named after the level that actually sets the zone, like Kept at's line.
  const fromAbove = inheritedZone(chain)
  const fromAboveZone = fromAbove?.zoneId
    ? ((zones.data ?? []).find((z) => z.id === fromAbove.zoneId)?.name ?? null)
    : null

  return (
    <Field label='Zone' help='Which walk a picker takes. Separate from Kept at.'>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger aria-label='Zone'>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value='INHERIT'>
            {fromAbove ? `Same as ${fromAbove.fromName} (${fromAboveZone ?? 'none'})` : 'None'}
          </SelectItem>
          {choices.map((z) => (
            <SelectItem key={z.id} value={z.id}>
              {z.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  )
}

/** How the grid's slots are named: two choices, an optional prefix, one preview line. */
function SlotNamesField({
  node,
  scheme,
  prefix,
  onChange,
}: {
  node: ApiStorageNode
  scheme: SlotLabelScheme | null
  prefix: string
  onChange: (next: { scheme: SlotLabelScheme; prefix: string }) => void
}) {
  const childKind = node.gridChildKind ?? 'POSITION'
  const preview = scheme
    ? slotNamePreview({ rows: node.gridRows ?? 1, columns: node.gridColumns ?? 1, scheme, prefix, childKind })
    : null

  return (
    <Field label='Slot names' help='Renames every slot. Stock follows, and the change is audited.'>
      <div className='flex flex-wrap items-center gap-2'>
        <div role='group' aria-label='Slot names' className='inline-flex rounded-md border p-0.5'>
          {SCHEMES.map((s) => (
            <button
              key={s.value}
              type='button'
              aria-pressed={scheme === s.value}
              onClick={() => onChange({ scheme: s.value, prefix })}
              className={cn(
                'rounded px-3 py-1.5 text-sm transition-colors',
                scheme === s.value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
        {scheme === 'SEQUENTIAL' && (
          <Input
            aria-label='Prefix'
            className='w-28'
            maxLength={8}
            value={prefix}
            placeholder={`Prefix (${DEFAULT_LABEL_PREFIX[childKind] ?? 'N'})`}
            onChange={(e) => onChange({ scheme, prefix: e.target.value })}
          />
        )}
      </div>
      {scheme && (
        <p className='text-sm'>
          {preview ? (
            <>
              <span className='text-muted-foreground'>Preview: </span>
              <span className='font-mono'>{preview}</span>
            </>
          ) : (
            <span className='text-destructive'>A prefix is up to 8 letters or digits.</span>
          )}
        </p>
      )}
    </Field>
  )
}

/** Move (part of the draft), Replace unit… and Delete… (their own dialogs). */
function OtherActions({
  node,
  nodes,
  parentId,
  onMove,
  onReplace,
  onDelete,
}: {
  node: ApiStorageNode
  /** Empty when the user may not move it. */
  nodes: ApiStorageNode[]
  parentId: string | null
  onMove: (parentId: string | null) => void
  onReplace?: () => void
  onDelete?: () => void
}) {
  const locate = useLocationText()
  const targets = node.parentId != null ? moveTargets(node, nodes) : []
  const moving = parentId !== node.parentId
  const [picking, setPicking] = useState(false)

  if (targets.length === 0 && !onReplace && !onDelete) return null

  return (
    <Section title='Other actions'>
      <div className='flex flex-wrap gap-2'>
        {targets.length > 0 && !picking && !moving && (
          <Button size='sm' variant='outline' onClick={() => setPicking(true)}>
            Move to another place in this unit…
          </Button>
        )}
        {onReplace && (
          <Button size='sm' variant='outline' onClick={onReplace}>
            Replace unit…
          </Button>
        )}
        {onDelete && (
          <Button size='sm' variant='outline' className='text-destructive hover:text-destructive' onClick={onDelete}>
            Delete…
          </Button>
        )}
      </div>
      {(picking || moving) && (
        <Field label='Move into' help='Moves on Save. Everything inside moves with it and is re-addressed.'>
          <div className='flex gap-2'>
            <Select value={moving && parentId ? parentId : ''} onValueChange={onMove}>
              <SelectTrigger aria-label='Move into'>
                <SelectValue placeholder='Choose where…' />
              </SelectTrigger>
              <SelectContent>
                {targets.map((n) => (
                  <SelectItem key={n.id} value={n.id}>
                    {locate(n.path)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size='sm'
              variant='ghost'
              className='h-10 shrink-0'
              onClick={() => {
                setPicking(false)
                onMove(node.parentId)
              }}
            >
              Keep it here
            </Button>
          </div>
        </Field>
      )}
    </Section>
  )
}
